import React, { useContext, useEffect, useReducer, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Store } from '../../Store';
import { getError } from '../../utils';
import LoadingBox from '../../components/LoadingBox';
import { useTranslation } from 'react-i18next';
import reservaService from '../../services/reserva.service';
import moment from 'moment';
import PageHeader from '../../components/shared/PageHeader';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { sorteosEnabledOf } from '../../lib/boardPlans';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loadingFetch: true };
    case 'FETCH_SUCCESS':
      return { ...state, reserva: action.payload, loadingFetch: false };
    case 'FETCH_FAIL':
      return { ...state, loadingFetch: false, errorFetch: action.payload };
    case 'CONFIRM_REQUEST':
      return { ...state, loadingConfirm: true };
    case 'CONFIRM_SUCCESS':
      return { ...state, loadingConfirm: false };
    case 'CONFIRM_FAIL':
      return { ...state, loadingConfirm: false, errorFetch: action.payload };
    default:
      return state;
  }
};

const inputCls =
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

export default function ConfirmarPago() {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [comprobante, setComprobante] = useState(null);
  const [titularCuenta, setTitular] = useState('');
  const [nroTransaccion, setNroTransaccion] = useState('');
  const [paymentType, setPaymentType] = useState('wire');

  const [{ loadingFetch, reserva, loadingConfirm }, dispatch] = useReducer(reducer, {
    reserva: {},
    loadingFetch: true,
    loadingConfirm: false,
    error: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const data = await reservaService.getReservaConTicketsAdmin(params.id);
        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    };
    void fetchData();
  }, [params.id]);

  const getTicketNumbers = () => {
    if (!reserva?.tickets?.length) return '';
    return reserva.tickets.join('-');
  };

  const getRifaDetails = () => {
    if (!reserva?.rifa) {
      return { nombre: '', raffleDate: '', description: '', price: 0, totalAmount: 0, sorteos: [] };
    }
    const rifa = reserva.rifa;
    const ticketCount = reserva.tickets?.length ?? 0;
    const precioRifa = rifa.precioTicket != null ? Number(rifa.precioTicket) : 0;
    const importeReserva = Number(reserva.importeReserva);

    let unitPrice = precioRifa > 0 ? precioRifa : 0;
    if (unitPrice === 0 && !Number.isNaN(importeReserva) && importeReserva > 0 && ticketCount > 0) {
      unitPrice = Math.round((importeReserva / ticketCount) * 100) / 100;
    }
    let totalAmount = 0;
    if (!Number.isNaN(importeReserva) && importeReserva > 0) {
      totalAmount = importeReserva;
    } else {
      totalAmount = unitPrice * ticketCount;
    }
    // Bonos without draws: no sorteo/premio info
    const sorteos = sorteosEnabledOf(rifa) ? rifa.sorteos || [] : [];
    const fechas = sorteos
      .map(s => s?.fecha && new Date(s.fecha))
      .filter(Boolean)
      .sort((a, b) => a - b);
    const drawDate = fechas.find(d => d > new Date()) || fechas[fechas.length - 1];
    return {
      nombre: rifa.nombre || '',
      raffleDate: drawDate ? drawDate.toLocaleDateString() : '',
      description: rifa.descripcion || '',
      price: unitPrice,
      totalAmount,
      sorteos,
    };
  };

  const getNextSorteo = sorteos => {
    if (!sorteos?.length) return null;
    const now = moment();
    return sorteos
      .filter(s => moment(s.fecha).isAfter(now))
      .sort((a, b) => moment(a.fecha).diff(moment(b.fecha)))[0];
  };

  const rifaDetails = getRifaDetails();
  const nextSorteo = rifaDetails.sorteos ? getNextSorteo(rifaDetails.sorteos) : null;

  const handleSubmit = async event => {
    event.preventDefault();
    if (paymentType === 'mercadopago') {
      await confirmMercadoPago();
      return;
    }
    await uploadComprobante();
  };

  const confirmMercadoPago = async () => {
    dispatch({ type: 'CONFIRM_REQUEST' });
    try {
      await reservaService.confirmarReserva(reserva._id, {
        responsableConfirmacion: userInfo,
        comprobante: null,
        nroTransaccion: '',
        titularCuenta: '',
      });
      dispatch({ type: 'CONFIRM_SUCCESS' });
      toast.success(t('reservation.purchaseConfirmed'));
      navigate('/AdminScreen/reservas');
    } catch (err) {
      dispatch({ type: 'CONFIRM_FAIL' });
      toast.error(getError(err));
    }
  };

  const uploadComprobante = async () => {
    if (!comprobante?.[0]) {
      toast.error(t('reservation.receiptRequired'));
      return;
    }
    const file = comprobante[0];
    const formData = new FormData();
    const r = Math.random().toString(36).substring(7);
    formData.append('file', file, file.name.replaceAll(' ', '') + r);

    dispatch({ type: 'CONFIRM_REQUEST' });
    try {
      const response = await reservaService.uploadComprobante(formData);
      await confirmarPago(response);
    } catch (err) {
      dispatch({ type: 'CONFIRM_FAIL' });
      toast.error(getError(err));
    }
  };

  const confirmarPago = async comp => {
    try {
      await reservaService.confirmarReserva(reserva._id, {
        responsableConfirmacion: userInfo,
        comprobante: comp,
        nroTransaccion,
        titularCuenta,
      });
      dispatch({ type: 'CONFIRM_SUCCESS' });
      toast.success(t('reservation.purchaseConfirmed'));
      navigate('/AdminScreen/reservas');
    } catch (err) {
      dispatch({ type: 'CONFIRM_FAIL' });
      toast.error(getError(err));
    }
  };

  return (
    <div className='space-y-6'>
      {(loadingFetch || loadingConfirm) && <LoadingBox overlay={true} />}

      <PageHeader
        title={t('reservation.confirmPayment')}
        actions={
          <Button variant='outline' onClick={() => navigate(-1)}>
            <ArrowLeft className='h-4 w-4' />
            {t('common.back')}
          </Button>
        }
      />

      <div className='mx-auto max-w-2xl space-y-6'>
        {/* Rifa summary card */}
        <Card>
          <CardHeader>
            <CardTitle>{t('raffle.details')}</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className='divide-y divide-border text-sm'>
              {[
                [t('raffle.raffle'), rifaDetails.nombre],
                [t('raffle.description'), rifaDetails.description],
                nextSorteo && [
                  t('raffle.nextDraw'),
                  `${moment(nextSorteo.fecha).format('DD/MM/YYYY')} — ${nextSorteo.titulo}`,
                ],
                rifaDetails.price > 0 && [t('raffle.price'), `$${rifaDetails.price}`],
                // 0-number bonos: no numbers row
                getTicketNumbers() && [t('ticket.number'), getTicketNumbers()],
                [t('reservation.paymentAmount'), `$${rifaDetails.totalAmount}`],
              ]
                .filter(Boolean)
                .map(([label, value]) => (
                  <div key={label} className='flex justify-between py-2'>
                    <span className='text-muted-foreground'>{label}</span>
                    <span className='font-medium'>{value}</span>
                  </div>
                ))}
            </dl>

            {nextSorteo?.premios?.length > 0 && (
              <div className='mt-4 border-t border-border pt-4'>
                <p className='mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                  {t('raffle.prizes')}
                </p>
                <ul className='space-y-1 text-sm'>
                  {nextSorteo.premios.map(premio => (
                    <li key={premio._id}>
                      {premio.puesto}° {t('raffle.prize')}: {premio.nombre} — {premio.descripcion}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment form */}
        <Card>
          <CardHeader>
            <CardTitle>{t('reservation.paymentMethod')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className='space-y-5'>
              {/* Payment type radios */}
              <div className='flex gap-6'>
                <label className='flex items-center gap-2 text-sm cursor-pointer'>
                  <input
                    type='radio'
                    name='paymentType'
                    checked={paymentType === 'wire'}
                    onChange={() => setPaymentType('wire')}
                    className='accent-primary'
                  />
                  {t('reservation.paymentTypeWire')}
                </label>
                <label className='flex items-center gap-2 text-sm cursor-pointer'>
                  <input
                    type='radio'
                    name='paymentType'
                    checked={paymentType === 'mercadopago'}
                    onChange={() => setPaymentType('mercadopago')}
                    className='accent-primary'
                  />
                  {t('reservation.paymentTypeMercadoPago')}
                </label>
              </div>

              {paymentType === 'mercadopago' && (
                <p className='text-sm text-muted-foreground'>
                  {t('reservation.mercadoPagoConfirmHint')}
                </p>
              )}

              {paymentType === 'wire' && (
                <div className='space-y-4'>
                  <div className='grid gap-4 sm:grid-cols-2'>
                    <div className='space-y-1.5'>
                      <label className='text-sm font-medium text-foreground'>
                        {t('reservation.transactionNumber')}
                      </label>
                      <input
                        type='text'
                        required
                        value={nroTransaccion}
                        onChange={e => setNroTransaccion(e.target.value)}
                        className={inputCls}
                      />
                    </div>
                    <div className='space-y-1.5'>
                      <label className='text-sm font-medium text-foreground'>
                        {t('reservation.accountHolder')}
                      </label>
                      <input
                        type='text'
                        required
                        value={titularCuenta}
                        onChange={e => setTitular(e.target.value)}
                        className={inputCls}
                      />
                    </div>
                  </div>
                  <div className='space-y-1.5'>
                    <label className='text-sm font-medium text-foreground'>
                      {t('reservation.receipt')}
                    </label>
                    <input
                      type='file'
                      required
                      onChange={e => setComprobante(e.target.files)}
                      className='flex w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm file:mr-3 file:border-0 file:bg-transparent file:text-sm file:font-medium focus:outline-none focus:ring-2 focus:ring-ring'
                    />
                  </div>
                </div>
              )}

              <div className='flex justify-end'>
                <Button type='submit' disabled={loadingConfirm}>
                  {t('common.confirm')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
